/**
 * L'account Clash Royale dell'utente, letto dai PROFILI di Limulo — per Decks e Battles.
 *
 * Il Sit&Go scrive il profilo di gioco su Limulo (`POST /v1/games/profiles`), mentre Decks
 * (`/cr/me`) e Battles (`/cr/profile/me`) chiedevano a QBot, che legge una tabella sua
 * (`SavedPlayer`): chi aveva collegato l'account dal Sit&Go vedeva ancora «Collega». Quando
 * Limulo e' la fonte (`lib/limulo/sorgente.ts`), «collegato» si decide QUI.
 *
 * 🔑 **Quattro esiti, e «non ho potuto leggere» NON e' «non collegato».** Collassarli manderebbe
 * a ricollegare un account che l'utente ha gia' — la stessa regola di `LinkedAccounts`
 * (`crSorgenteMuta`). Il banner «collega» compare solo su `notLinked`, che si dice SOLO a
 * risposta ricevuta da entrambe le rotte e col gioco Clash Royale trovato nel catalogo.
 *
 * ⚠️ Il gioco si cerca per NOME (`limuloClashRoyaleGame`) e l'URL di creazione usa il CODICE
 * (`gameId`), mai l'`_id`: con un ObjectId tre rotte su cinque rispondono 424. Se il codice
 * manca non si offre una strada che porterebbe a un 424: si ripiega sull'elenco profili.
 */

import type { LimuloGame, LimuloProfile } from '../../types/limulo.ts';
import { limuloClashRoyaleGame, limuloProfilesForGame } from './profiles.ts';
import { LIMULO_PROFILES_URL, limuloProfileCreateUrl } from './routes.ts';

/** Lo stato di una query RTK, ridotto a cio' che serve: il dato, l'attesa, il rifiuto. */
export interface QueryLetta<T> {
  data?: T | undefined;
  isLoading: boolean;
  isError: boolean;
}

export type CrLinkDaLimulo =
  /** Limulo non e' la fonte: questo modulo non ha niente da dire, decide QBot come prima. */
  | { kind: 'off' }
  /** Ancora nessuna risposta: niente banner, niente errore. */
  | { kind: 'loading' }
  /** Non ho potuto leggere (rete, catalogo senza Clash Royale): un errore di lettura, MAI il banner. */
  | { kind: 'unknown'; motivo: 'lettura' }
  /**
   * PIU' DI UN profilo CR attivo: QBot risponde «non collegato» (nessuna scelta per ordine di lista,
   * `limuloTag.ts`, #251) e la TMA non sa disattivarne uno. Non e' «collegato» (Decks direbbe «arriva a
   * breve» per sempre) ne' «non collegato» (un «Collega» ne creerebbe un terzo): si dice il fatto.
   */
  | { kind: 'unknown'; motivo: 'piuProfili'; n: number }
  /** C'e' il profilo CR. `tag` e' l'`externalPlayerId` senza `#` (la forma che QBot vuole), `''` se manca. */
  | { kind: 'linked'; profile: LimuloProfile; tag: string }
  /** Risposta ricevuta e il profilo CR non c'e': l'unico caso in cui si dice «collega». */
  | { kind: 'notLinked'; createHref: string };

/** Il tag CR come lo vuole `GET /battles/:tag` di QBot: senza `#` iniziale. */
export function tagDaProfilo(profile: Pick<LimuloProfile, 'externalPlayerId'>): string {
  return (profile.externalPlayerId ?? '').trim().replace(/^#/, '');
}

export function crLinkDaLimulo(
  isTheSource: boolean,
  profili: QueryLetta<readonly LimuloProfile[]>,
  giochi: QueryLetta<readonly LimuloGame[]>,
): CrLinkDaLimulo {
  if (!isTheSource) return { kind: 'off' };

  // Un dato arrivato vale anche se un giro successivo e' fallito (refetch al ritorno in
  // primo piano): lo stato lo dice il dato, non l'ultimo tentativo.
  const senzaDato = (q: QueryLetta<unknown>) => q.data === undefined;
  if (senzaDato(profili) || senzaDato(giochi)) {
    // Senza dato e senza un'attesa in corso e' «non lo so»: un rifiuto, ma anche una query in
    // `skip`, che non carica e non risponde e sarebbe «loading» per sempre.
    const muta = (q: QueryLetta<unknown>) => senzaDato(q) && (q.isError || !q.isLoading);
    return muta(profili) || muta(giochi) ? { kind: 'unknown', motivo: 'lettura' } : { kind: 'loading' };
  }

  const cr = limuloClashRoyaleGame(giochi.data);
  // Catalogo arrivato ma senza Clash Royale: non e' «non hai il profilo», e non si puo' nemmeno
  // crearlo da qui. Non si afferma niente.
  if (!cr) return { kind: 'unknown', motivo: 'lettura' };

  // 🔑 «Attivo» lo decide il SERVER, non un letterale nostro: la lettura chiede `activeOnly: true`
  // (`hooks/useCrLinkDaLimulo.ts`), come QBot #251. `inactive` NON e' uno stato di Limulo (gli stati
  // sono `active | suspended | archived`, `app-games/domain.js`), e un filtro su un valore che non
  // esiste non agisce mai: prima i profili spenti restavano fuori solo per un default del BFF
  // (`activeOnly ?? true`). Nessun filtro su `status` qui: non invento un letterale che nessuno ha visto.
  //
  // 🔑 PIU' DI UN profilo CR attivo: come QBot (`limuloTag.ts`: «esattamente UNO», nessuna scelta per
  // ordine di lista) NON si sceglie il primo. Diventa `unknown/piuProfili`, e cambia solo la frase.
  const dellGioco = limuloProfilesForGame(profili.data, cr);
  if (dellGioco.length > 1) return { kind: 'unknown', motivo: 'piuProfili', n: dellGioco.length };
  const profile = dellGioco[0];
  if (profile) return { kind: 'linked', profile, tag: tagDaProfilo(profile) };
  return {
    kind: 'notLinked',
    createHref: cr.gameId ? limuloProfileCreateUrl(cr.gameId) : LIMULO_PROFILES_URL,
  };
}

/**
 * True quando un errore di QBot dice «nessun account collegato» (`NOT_LINKED`). Nel ramo Limulo NON
 * e' un fatto sull'utente: QBot legge una tabella sua, non il profilo di Limulo. Serve a chi deve
 * dire «arriva a breve» invece di «collega» o «riprova».
 *
 * ⛔ NON `NOT_FOUND`, e solo col `404`: e' «il tag non e' mappato», un guasto che non passa da solo. Nel ramo Limulo,
 * con un tag sbagliato, lo si direbbe un'attesa infinita («arriva a breve») invece di lasciargli
 * «non sono riuscito a caricare» + «riprova» (revisione di #506, 🟢).
 */
export function isQbotNotLinkedCode(error: unknown): boolean {
  const e = error as { status?: number; data?: { error?: { code?: string } } } | undefined;
  // `status === 404` come `AdvisorOverlay`: QBot risponde anche `401 NOT_LINKED identity_missing`
  // (`routes/profile.ts`, `routes/advisor.ts`), che e' un'altra cosa — non un'attesa (revisione, 🟢10).
  return e?.status === 404 && e?.data?.error?.code === 'NOT_LINKED';
}
